import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import type { SleepMode } from '../ListenContext.js';

export interface UseSleepTimerReturn {
  sleepMode: SleepMode | null;
  /** Segundos restantes del timer (`null` en modo `eoc` o sin timer). */
  sleepSecs: number | null;
  setSleep: (mode: SleepMode, mins?: number) => void;
  cancelSleep: () => void;
}

/**
 * Temporizador de sueño del player. La cuenta regresiva corre sólo mientras
 * reproduce; al llegar a 0 pausa y se autocancela. El modo 'eoc' (fin del
 * capítulo) lo maneja el player en `onEnded` (no auto-avanza). Pausar
 * manualmente congela la cuenta.
 */
export function useSleepTimer(args: {
  playing: boolean;
  audioRef: RefObject<HTMLAudioElement | null>;
}): UseSleepTimerReturn {
  const { playing, audioRef } = args;

  const [sleepMode, setSleepMode] = useState<SleepMode | null>(null);
  const [sleepSecs, setSleepSecs] = useState<number | null>(null);

  const setSleep = useCallback((mode: SleepMode, mins?: number): void => {
    if (mode === 'eoc') {
      setSleepMode('eoc');
      setSleepSecs(null);
    } else {
      setSleepMode('timer');
      setSleepSecs(Math.max(1, Math.round((mins ?? 0) * 60)));
    }
  }, []);
  const cancelSleep = useCallback((): void => {
    setSleepMode(null);
    setSleepSecs(null);
  }, []);

  useEffect(() => {
    if (sleepMode !== 'timer' || !playing) return;
    const id = setInterval(() => {
      setSleepSecs((s) => {
        if (s == null) return null;
        if (s <= 1) {
          audioRef.current?.pause();
          setSleepMode(null);
          return null;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [sleepMode, playing, audioRef]);

  return { sleepMode, sleepSecs, setSleep, cancelSleep };
}
